import React, { useState, useEffect, useReducer, useCallback, useMemo } from 'react';
import { FormGroup, Input, Button, Card, InputGroup, Label } from 'reactstrap';
import _isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RadioCheckGroup } from '../../../academic-affair-report/components/RadioCheckGroup';

import {
    faSearch,
    faPlus,
    faUndo,
    faFileExport,
    faFile,
} from '@fortawesome/free-solid-svg-icons';
import { academicProgramsService, generateReportService } from '../../../../services';
import { onChangeFunction, usePermissions } from 'shared';
import { FormSelect } from '../../../blocks';
import Select from 'react-select';
import {
    deriveKey,
    getCipLabel,
    containsValue,
} from '../../../academic-affair-report/program-criteria-form';
import { INITIAL_FORM } from 'components/academic-affair-report/helpers';
import eventEmitter from "../../../../shared/eventEmitter";

const makeShortDate = date => {
    if (!date) return '';
    const parsedDate = new Date(date);
    return parsedDate.toISOString().split('T')[0];
};
const initialValues = {
    pendingReview: false,
    institutionLevels: [],
    institutionControls: [],
    institutions: [],
    requestType: [],
    degreeLevels: [],
    twoDigitCips: [],
    fourDigitCips: [],
    sixDigitCips: [],
    distanceEducationId: [],
    distanceEducationOnlineId: [],
    programName: '',
    programId: '',
    programStatus: [],
    maxApprovalYear: new Date().getFullYear().toString(),
    minApprovalYear: '1971',
    monthYearIdentifier: null,
    distanceEducation: null,
    stemPff: null,
    workforceReadyGrant: null,
    separatePages: false,
    beforeDate: '',
    afterDate: '',
    exportStatus: [],
};

const originalCIPList = {
    twoCip: [],
    fourCip: [],
    sixCip: [],
};
const convertCipToValue = d => ({ ...d, value: d.cipCode });

const getControlsFromInstitutionTypes = institutionTypes => {
    return Array.from(new Set(institutionTypes.map(x => x.controlCode)))
        .map(code => institutionTypes.find(obj => obj.controlCode === code))
        .reduce((controls, item) => {
            controls.push({ key: item.controlCode, value: item.controlName });
            return controls;
        }, []);
};
const getLevelsFromInstitutionTypes = institutionTypes => {
    return Array.from(new Set(institutionTypes.map(x => x.levelCode)))
        .map(code => institutionTypes.find(obj => obj.levelCode === code))
        .reduce((levels, item) => {
            levels.push({ key: item.levelCode, value: item.levelName });
            return levels;
        }, []);
};

export const getOptionLabel = d => d.value;

export const SearchAdminProgramRequests = ({
    setTabData,
    setTabHeaders,
    tabHeaders,
}) => {
    const submitPermission = usePermissions({
        permissions: ['submit-new-program'],
    });
    const [fetchingInstitutions, setFetchingInstitutions] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toggle = () => setIsModalOpen(!isModalOpen);
    const reducer = (state, newState) => ({ ...state, ...newState });

    const [userInput, setUserInput] = useReducer(reducer, initialValues);

    const [options, setOptions] = useReducer(reducer, []);

    useEffect(() => {
        const fetchNewInstitutions = async () => {
            setFetchingInstitutions(true);
            try {
                const query = queryString.stringify(
                    {
                        institutionLevels: userInput.institutionLevels
                            ? deriveKey(userInput.institutionLevels, 'value')
                            : [],
                        institutionControls: userInput.institutionControls
                            ? deriveKey(userInput.institutionControls, 'value')
                            : [],
                        systems: userInput.systems
                            ? deriveKey(userInput.systems, 'value')
                            : [],
                        getAllInstitutions: true,
                    },
                    {
                        arrayFormat: 'separator',
                        skipEmptyString: true,
                    }
                );
                const data = await academicProgramsService.getInstitutions(query);
                setUserInput({ institutions: [] });
                setOptions({
                    institutions: data,
                });
            } catch (error) {
                console.log(error);
            }
            setFetchingInstitutions(false);
        };
        fetchNewInstitutions();
    }, [userInput.institutionControls, userInput.institutionLevels, userInput.systems]);

    const handleUserInputChange = e => {
        const { name, value } = e.target;

        setUserInput(prevInput => ({
            ...prevInput,
            [name]: value,
        }));
    };

    useEffect(() => {
        if (options.institutions) {
            if (options.institutions.length == 1) {
                const singleIns = {
                    label: options.institutions[0].value,
                    value: options.institutions[0].label,
                };
                setUserInput((userInput.institutions = [singleIns]));
            }
        }
    }, [options.institutions]);

    useEffect(() => {
        UpdateCipValue();
    }, [userInput.twoDigitCips]);

    useEffect(() => {
        if (userInput.fourDigitCips) {
            if (userInput.fourDigitCips.length > 0) {
                setOptions({
                    sixCip: GetSubstringOfCip(
                        5,
                        userInput.fourDigitCips,
                        originalCIPList.sixCip
                    ),
                });
            } else {
                UpdateCipValue();
            }
        } else {
            UpdateCipValue();
        }
    }, [userInput.fourDigitCips]);

    const UpdateCipValue = () => {
        if (userInput.twoDigitCips) {
            if (userInput.twoDigitCips.length > 0) {
                setOptions({
                    fourCip: GetSubstringOfCip(
                        2,
                        userInput.twoDigitCips,
                        originalCIPList.fourCip
                    ),
                    sixCip: GetSubstringOfCip(
                        2,
                        userInput.twoDigitCips,
                        originalCIPList.sixCip
                    ),
                });
            } else {
                setOptions({
                    twoCip: originalCIPList.twoCip,
                    fourCip: originalCIPList.fourCip,
                    sixCip: originalCIPList.sixCip,
                });
            }
        } else {
            setOptions({
                twoCip: originalCIPList.twoCip,
                fourCip: originalCIPList.fourCip,
                sixCip: originalCIPList.sixCip,
            });
        }
    };

    const GetSubstringOfCip = (stringLength, smallerDigitCipList, fullCipList) => {
        const smallerDigitCipListValue = smallerDigitCipList.map(currentValue => {
            return currentValue.value;
        });
        return fullCipList.filter(cip =>
            smallerDigitCipListValue.includes(cip.cipCode.substring(0, stringLength))
        );
    };

    const formatRequestData = res => {
        const data = res?.reduce((list, obj) => {
            if (!list['approved']) {
                list['approved'] = [];
                list['review'] = [];
                list['denied'] = [];
                list['deleted'] = [];
                list['submitted'] = [];
                list['programRequests'] = [];
            }
            // adding is selected for checkbox on admin page
            const updatedObj = { ...obj, isSelected: false, isAdmin: true };
            if (obj.status === 'Denied') list['denied'].push(updatedObj);
            if (obj.status === 'Deleted') list['deleted'].push(updatedObj);
            if (obj.status === 'Awaiting Institution' || obj.status === 'Under Review')
                list['review'].push(updatedObj);
            if (obj.status === 'Approved') list['approved'].push(updatedObj);
            if (obj.status === 'Submitted') list['submitted'].push(updatedObj);
            list['programRequests'].push(updatedObj);
            return list;
        }, {});
        if (data) {
            const updatedHeaders = tabHeaders.map(header => ({
                ...header,
                count: data[header.id]?.length || '0',
            }));
            setTabHeaders(updatedHeaders);
        }
        setTabData(data);
    };

    useEffect(() => {
        const handleDataUpdate = async () => {
            console.log("logging for admin search page")
            const res = await academicProgramsService.getAllProgramRequests();
            formatRequestData(res);
        };
        eventEmitter.on('updateSARData', handleDataUpdate);
        return () => {
            eventEmitter.off('updateSARData', handleDataUpdate);
        };
    }, []);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const allSARReqs = await academicProgramsService.getAllProgramRequests();
                formatRequestData(allSARReqs);
                const getAllQuery = queryString.stringify(
                    { getAllInstitutions: true },
                    {
                        arrayFormat: 'separator',
                        skipEmptyString: true,
                    }
                );
                const res = await Promise.all([
                    academicProgramsService.getInstitutions(getAllQuery),
                    academicProgramsService.getInstitutionTypes(),
                    academicProgramsService.getCips(),
                    academicProgramsService.getDegreeLevels(),
                    academicProgramsService.getDistanceEds(),
                    academicProgramsService.getDistanceEdsOnline(),
                    academicProgramsService.getProgramRequestTypes(),
                ]);

                res[1].push({
                    controlCode: 'BPE',
                    controlName: 'BPE-Authorized',
                    levelCode: '4',
                    levelName: '4-year or above',
                });
                res[1].push({
                    controlCode: 'ICI',
                    controlName: 'ICI-Authorized',
                    levelCode: '4',
                    levelName: '4-year or above',
                });
                await setOptions({
                    institutions: res[0],
                    institutionControls: getControlsFromInstitutionTypes(res[1]),
                    institutionLevels: getLevelsFromInstitutionTypes(res[1]),
                    twoCip: res[2][0].map(convertCipToValue),
                    fourCip: res[2][1].map(convertCipToValue),
                    sixCip: res[2][2].map(convertCipToValue),
                    degreeLevels: res[3],
                    distanceEds: res[4],
                    distanceEdsOnline: res[5],
                    requestTypes: res[6],
                });
                originalCIPList.twoCip = res[2][0].map(convertCipToValue);
                originalCIPList.fourCip = res[2][1].map(convertCipToValue);
                originalCIPList.sixCip = res[2][2].map(convertCipToValue);
            } catch (error) {}
        };
        fetchOptions();
    }, []);

    const extractLabelsFromResponseCSV = unextractedResponseValues => {
        return unextractedResponseValues.map(currentValue => {
            return currentValue.label;
        });
        //.join(',');
    };

    const extractValuesFromResponseCSV = unextractedResponseValues => {
        return unextractedResponseValues.map(currentValue => {
            return currentValue.value;
        });
        //.join(',');
    };

    const CreateSearchPostData = () => {
        const searchPostData = JSON.parse(
            JSON.stringify({
                pendingReview: false,
                institutionControls:
                    userInput.institutionControls &&
                        userInput.institutionControls.length > 0
                        ? extractLabelsFromResponseCSV(userInput.institutionControls)
                        : undefined,
                institutionLevels:
                    userInput.institutionLevels && userInput.institutionLevels.length > 0
                        ? extractLabelsFromResponseCSV(userInput.institutionLevels)
                        : undefined,
                institutions:
                    userInput.institutions && userInput.institutions.length > 0
                        ? extractValuesFromResponseCSV(userInput.institutions)
                        : undefined,
                degreeLevels:
                    userInput.degreeLevels && userInput.degreeLevels.length > 0
                        ? extractValuesFromResponseCSV(userInput.degreeLevels)
                        : undefined,
                twoDigitCips:
                    userInput.twoDigitCips && userInput.twoDigitCips.length > 0
                        ? extractValuesFromResponseCSV(userInput.twoDigitCips)
                        : undefined,
                fourDigitCips:
                    userInput.fourDigitCips && userInput.fourDigitCips.length > 0
                        ? extractValuesFromResponseCSV(userInput.fourDigitCips)
                        : undefined,
                sixDigitCips:
                    userInput.sixDigitCips && userInput.sixDigitCips.length > 0
                        ? extractValuesFromResponseCSV(userInput.sixDigitCips)
                        : undefined,
                programName: userInput.programName ? userInput.programName : undefined,
                programId: userInput.programId || null,
                separatePages: userInput.separatePages,
                beforeDate: userInput.beforeDate,
                afterDate: userInput.afterDate,
                requestTypes:
                    userInput.requestTypes && userInput.requestTypes.length > 0
                        ? extractValuesFromResponseCSV(userInput.requestTypes)
                        : undefined,
                requestStatuses:
                    userInput.exportStatus && userInput.exportStatus.length > 0
                        ? extractValuesFromResponseCSV(userInput.exportStatus)
                        : undefined
            })
        );
        return searchPostData;
    };

    const submitForm = () => {
        const searchPostData = CreateSearchPostData();
        Promise.resolve(
            academicProgramsService.searchProgramRequests(searchPostData)
        ).then(postRes => {
            formatRequestData(postRes);
            return postRes;
        });
    };

    const submitFormAndExport = () => {
        const searchPostData = CreateSearchPostData();

        Promise.resolve(generateReportService.getProgramRequestForExport(searchPostData));
        // userInput.stemPff.value
        Promise.resolve(
            academicProgramsService.searchProgramRequests(searchPostData)
        ).then(postRes => {
            formatRequestData(postRes);
            return postRes;
        });
    };

    const fourCipOptions = useMemo(() => {
        const value = userInput?.twoDigitCips?.value;
        if (!value) return options.fourCip;
        return options?.fourCip?.filter(cip =>
            containsValue(cip.value, [value], d => d + '.')
        );
    }, [options.fourCip, userInput.twoDigitCips]);
    const sixCipOptions = useMemo(() => {
        const twoVal = userInput?.twoDigitCips?.value;
        const fourVal = userInput?.fourDigitCips?.value;
        let filtered = options?.sixCip || [];
        if (twoVal)
            filtered = filtered.filter(cip =>
                containsValue(cip.value, [twoVal], d => d + '.')
            );
        if (fourVal)
            filtered = filtered.filter(cip => containsValue(cip.value, [fourVal]));
        return filtered;
    }, [options.sixCip, userInput.twoDigitCips, userInput.fourDigitCips]);

    return (
        <React.Fragment>
            <Card className="card mb-3 p-3">
                <h4 className="text-muted mb-4">Search Program Requests</h4>
                <FormSelect
                    label="Institution Level"
                    onChange={setUserInput}
                    options={
                        options.institutionLevels
                            ? options.institutionLevels.map(x => {
                                return { label: x.value, value: x.key };
                            })
                            : []
                    }
                    placeholder="All"
                    name="institutionLevels"
                    value={userInput.institutionLevels}
                    // getOptionLabel={getOptionLabel}
                    isMulti
                />
                <FormSelect
                    label="Institution Type"
                    onChange={setUserInput}
                    options={
                        options.institutionControls
                            ? options.institutionControls.map(x => {
                                return { label: x.value, value: x.key };
                            })
                            : []
                    }
                    placeholder="All"
                    name="institutionControls"
                    value={userInput.institutionControls}
                    isMulti
                />
                <FormSelect
                    label="Institutions"
                    onChange={setUserInput}
                    options={
                        options.institutions
                            ? options.institutions.map(x => {
                                return { label: x.value, value: x.label };
                            })
                            : []
                    }
                    placeholder="All"
                    name="institutions"
                    value={userInput.institutions}
                    loading={fetchingInstitutions}
                    //isDisabled={userInput.institutionTypes.length === 0}
                    isMulti
                />

                <FormSelect
                    label="Request Type"
                    onChange={setUserInput}
                    options={
                        options.requestTypes
                            ? options.requestTypes.map(x => {
                                return { label: x.typeDesc, value: x.typeId };
                            })
                            : []
                    }
                    placeholder="All"
                    name="requestTypes"
                    value={userInput.requestTypes}
                    loading={fetchingInstitutions}
                    isMulti
                />

                <div className="form-row align-items-center">
                    <div className="col-lg-8">
                        <FormGroup>
                            <label>Program Name</label>
                            <Input
                                type="text"
                                placeholder="Program Name"
                                onChange={evt =>
                                    onChangeFunction.handleChange(evt, setUserInput)
                                }
                                name="programName"
                                value={userInput.programName}
                            />
                        </FormGroup>
                    </div>
                    <div className="col-lg-4">
                        <FormGroup>
                            <label>ID</label>
                            <Input
                                type="text"
                                placeholder="Program ID"
                                onChange={evt =>
                                    onChangeFunction.handleChange(evt, setUserInput)
                                }
                                name="programId"
                                value={userInput.programId}
                            />
                        </FormGroup>
                    </div>
                </div>

                <div className="form-row mt-3">
                    <div className="col-lg">
                        <label className="d-block">Submitted Dates:</label>

                        <InputGroup>
                            <Input
                                type="date"
                                className="form-control datepicker"
                                value={makeShortDate(userInput.afterDate)}
                                onChange={evt =>
                                    onChangeFunction.handleChange(evt, setUserInput)
                                }
                                name="afterDate"
                            />
                            <div className="input-group-append">
                                <span className="input-group-text">to</span>
                            </div>
                            <Input
                                type="date"
                                className="form-control datepicker"
                                value={makeShortDate(userInput.beforeDate)}
                                onChange={evt =>
                                    onChangeFunction.handleChange(evt, setUserInput)
                                }
                                name="beforeDate"
                            />
                        </InputGroup>
                    </div>
                </div>

                <div className="form-row mt-3">
                    <div className="col-xl-12">
                        <FormSelect
                            label="Degree/Award Level"
                            onChange={setUserInput}
                            options={
                                options.degreeLevels
                                    ? options.degreeLevels.map(x => {
                                        return { label: x.value, value: x.label };
                                    })
                                    : []
                            }
                            placeholder="All"
                            name="degreeLevels"
                            value={userInput.degreeLevels}
                            // getOptionLabel={getOptionLabel}
                            isMulti={true}
                            isClearable
                        />
                    </div>
                </div>
                <div className="form-row">
                    <FormSelect
                        label="Two Digit CIP"
                        onChange={setUserInput}
                        options={
                            options.twoCip
                                ? options.twoCip.map(x => {
                                    return {
                                        label: x.cipCode + ' - ' + x.cipName,
                                        value: x.cipCode,
                                    };
                                })
                                : []
                        }
                        placeholder="All"
                        name="twoDigitCips"
                        value={userInput.twoDigitCips}
                        className="col-12"
                        // getOptionLabel={getCipLabel}
                        showLoading
                        isMulti={true}
                        isClearable
                    />
                    <FormSelect
                        label="Four Digit CIP"
                        onChange={setUserInput}
                        options={
                            fourCipOptions
                                ? fourCipOptions.map(x => {
                                    return {
                                        label: x.cipCode + ' - ' + x.cipName,
                                        value: x.cipCode,
                                    };
                                })
                                : []
                        }
                        placeholder="All"
                        name="fourDigitCips"
                        value={userInput.fourDigitCips}
                        className="col-12"
                        // getOptionLabel={getCipLabel}
                        showLoading
                        isMulti={true}
                        isClearable
                    />
                    <FormSelect
                        label="Six Digit CIP"
                        onChange={setUserInput}
                        options={
                            sixCipOptions
                                ? sixCipOptions.map(x => {
                                    return {
                                        label: x.cipCode + ' - ' + x.cipName,
                                        value: x.cipCode,
                                    };
                                })
                                : []
                        }
                        placeholder="All"
                        name="sixDigitCips"
                        value={userInput.sixDigitCips}
                        className="col-12"
                        // getOptionLabel={getCipLabel}
                        showLoading
                        isMulti={true}
                        isClearable
                    />
                    <FormSelect
                        label="Statuses To Export"
                        onChange={setUserInput}
                        options={[
                            { label: "Submitted", value: '1' },
                            { label: "Under Review", value: '2' },
                            { label: "Approved", value: '3' },
                            { label: "Denied", value: '4' },
                            { label: "Deleted", value: '6' }
                        ]}
                        placeholder="All"
                        name="exportStatus"
                        value={userInput.exportStatus}
                        className="col-12"
                        showLoading
                        isMulti={true}
                        isClearable
                    />
                </div>
                <button
                    className="btn btn-block btn-sm btn-outline-primary"
                    onClick={() => setUserInput(initialValues)}
                >
                    <FontAwesomeIcon icon={faUndo} /> Reset Form
                </button>
                <button
                    className="btn btn-block btn-primary"
                    color="success"
                    onClick={() => {
                        submitForm();
                    }}
                >
                    <FontAwesomeIcon icon={faSearch} />
                    &nbsp;Search
                </button>
                <button
                    className="btn btn-block btn-primary"
                    color="success"
                    onClick={() => {
                        submitFormAndExport();
                    }}
                >
                    <FontAwesomeIcon icon={faFileExport} />
                    &nbsp;Search and Export to File
                </button>
            </Card>
        </React.Fragment>
    );
};
